import { apiFetch, apiFetchJson, type ApiRequestInit } from '@wundo/client/api.ts';
import type { UserSkillOverview } from '@wundo/shared/userSkillOverview.ts';
import type { TrackParticipant } from '@/types/cohorts.ts';
import type { ChatSession, ChatSessionMessage } from '@/types/messages.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import type { User, UserMemory } from '@/types/users.ts';

/**
 * A user as the detail endpoint answers it: the list record plus the one field
 * only this screen asks about — when they asked us to stop mailing them.
 */
export interface UserRecord extends User {
  email_opt_out_at: string | null;
}

/**
 * Everything `/users` filters, sorts and pages by. All of it optional and all
 * of it server-side: the list runs into five figures, so nothing here is done
 * in the browser.
 */
export interface UserListParams {
  page?: number;
  limit?: number;
  organisationId?: string;
  cohortId?: string;
  query?: string;
  role?: string;
  status?: string;
  channel?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function userListQuery(params: UserListParams): string {
  const search = new URLSearchParams();

  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.organisationId) search.set('organisation_id', params.organisationId);
  if (params.cohortId) search.set('cohort_id', params.cohortId);
  if (params.query) search.set('query', params.query);
  if (params.status) search.set('status', params.status);
  if (params.channel) search.set('channel', params.channel);
  if (params.role) search.set('role', params.role);
  if (params.sortBy) {
    search.set('sort_by', params.sortBy);
    search.set('sort_order', params.sortOrder ?? 'asc');
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

/**
 * `init` carries the abort signal a search field hands over when its answer
 * stops being the one it is waiting for. Every other caller ignores it.
 */
export async function listUsers(
  params: UserListParams = {},
  init: ApiRequestInit = {},
): Promise<PaginatedResponse<User>> {
  return apiFetchJson<PaginatedResponse<User>>(`/users${userListQuery(params)}`, init);
}

export async function getUser(id: string): Promise<UserRecord> {
  return apiFetchJson<UserRecord>(`/users/${id}`);
}

/**
 * The fields the console writes. Not the whole record: `status` only exists on
 * an existing user, and everything the backend derives is its own business.
 */
export interface UserWrite {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  language: string;
  /** Only ever `default`: "Automatic", handing the language to the first contact. */
  language_source: 'default';
  role: 'superadmin' | 'admin' | 'user' | 'guest' | 'service';
  channel: 'web' | 'msteams' | 'slack';
  notification_channel: 'msteams' | 'slack' | 'email' | 'none';
  organisation_id: string;
  status: 'onboarded' | 'active' | 'inactive' | 'expired';
  excluded_from_metrics: boolean;
  timezone: string;
  notification_time: string;
}

/** A cohort given here replaces the organisation's default one. */
export type UserCreate = Partial<UserWrite> & { cohort_id?: string };

export async function createUser(body: UserCreate): Promise<User> {
  return apiFetchJson<User>('/users', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateUser(id: string, body: Partial<UserWrite>): Promise<User> {
  return apiFetchJson<User>(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Put someone back on the list after they unsubscribed by mistake.
 *
 * The ordinary user update, not an endpoint of its own: the flag is a column
 * on the user, and clearing it is superadmin-only for the same reason nobody
 * else may write it — this is the only way back, and it must be a person
 * asking us, not a button on a page anyone can reach.
 */
export async function resubscribeToPlatformEmail(userId: string): Promise<void> {
  await apiFetch(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ email_opt_out_at: null }),
  });
}

export async function deleteUser(id: string): Promise<void> {
  await apiFetch(`/users/${id}`, { method: 'DELETE' });
}

/**
 * The one cohort this person is in, as a list.
 *
 * A user may hold at most one enrolment, but the endpoint is the pivot table's
 * and answers with a collection — the detail page reads the first row and
 * treats a second one as an anomaly it does not have to draw.
 */
export async function listUserTrackParticipants(
  userId: string,
): Promise<TrackParticipant[]> {
  const { data } = await apiFetchJson<{ data: TrackParticipant[] }>(
    `/track-participants?user_id=${userId}`,
  );
  return data;
}

/** The conversations this person had with Wundo, newest first. */
export async function listUserChatSessions(userId: string): Promise<ChatSession[]> {
  const { data } = await apiFetchJson<{ data: ChatSession[] }>(
    `/chat-sessions?user_id=${userId}`,
  );
  return data;
}

export async function listChatSessionMessages(
  sessionId: string,
): Promise<ChatSessionMessage[]> {
  const { data } = await apiFetchJson<{ data: ChatSessionMessage[] }>(
    `/chat-sessions/${sessionId}/messages`,
  );
  return data;
}

/** A user's stored memories, most recently updated first (superadmin only). */
export async function listUserMemories(userId: string): Promise<UserMemory[]> {
  const { data } = await apiFetchJson<{ data: UserMemory[] }>(
    `/memories/user/${userId}?limit=100`,
  );
  return data;
}

/** Everything the skills tab draws: active skill, usage, schedules, runs. */
export async function fetchUserSkillOverview(userId: string): Promise<UserSkillOverview> {
  return apiFetchJson<UserSkillOverview>(`/users/${userId}/skill-overview`);
}

/** Cancels a reminder or check-in. The user is not told. */
export async function deactivateSkillSchedule(scheduleId: string): Promise<void> {
  await apiFetch(`/skill-schedules/${scheduleId}/actions/deactivate`, { method: 'POST' });
}

export function userTrackParticipantsKey(userId: string): string[] {
  return ['users', userId, 'track-participants'];
}

export function userSkillOverviewKey(userId: string): string[] {
  return ['users', userId, 'skill-overview'];
}
